'use client';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'warning';
import pdfjs from "../pdfjs.js";
import usePageContext from "../shared/hooks/usePageContext.js";
import useResolver from "../shared/hooks/useResolver.js";
import { cancelRunningTask } from "../shared/utils.js";
function isTextItem(item) {
  return 'str' in item;
}
export default function TextLayer() {
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var customTextRenderer = pageContext.customTextRenderer,
    onGetTextError = pageContext.onGetTextError,
    onGetTextSuccess = pageContext.onGetTextSuccess,
    onRenderTextLayerError = pageContext.onRenderTextLayerError,
    onRenderTextLayerSuccess = pageContext.onRenderTextLayerSuccess,
    page = pageContext.page,
    pageIndex = pageContext.pageIndex,
    pageNumber = pageContext.pageNumber,
    rotate = pageContext.rotate,
    scale = pageContext.scale;
  invariant(page, 'Attempted to load page text content, but no page was specified.');
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    textContentState = _useResolver2[0],
    textContentDispatch = _useResolver2[1];
  var textContent = textContentState.value,
    textContentError = textContentState.error;
  var layerElement = useRef(null);
  var endElement = useRef();
  warning(parseInt(window.getComputedStyle(document.body).getPropertyValue('--react-pdf-text-layer'), 10) === 1, 'TextLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-text-layer');
  /**
   * Called when a page text content is read successfully
   */
  function onLoadSuccess() {
    if (!textContent) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onGetTextSuccess) {
      onGetTextSuccess(textContent);
    }
  }
  /**
   * Called when a page text content failed to read successfully
   */
  function onLoadError() {
    if (!textContentError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, textContentError.toString());
    if (onGetTextError) {
      onGetTextError(textContentError);
    }
  }
  function resetTextContent() {
    textContentDispatch({
      type: 'RESET'
    });
  }
  useEffect(resetTextContent, [page, textContentDispatch]);
  function loadTextContent() {
    if (!page) {
      return;
    }
    var cancellable = makeCancellable(page.getTextContent());
    var runningTask = cancellable;
    cancellable.promise.then(function (nextTextContent) {
      textContentDispatch({
        type: 'RESOLVE',
        value: nextTextContent
      });
    }).catch(function (error) {
      textContentDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      return cancelRunningTask(runningTask);
    };
  }
  useEffect(loadTextContent, [page, textContentDispatch]);
  useEffect(function () {
    if (textContent === undefined) {
      return;
    }
    if (textContent === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  },
  // Ommitted callbacks so they are not called every time they change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [textContent]);
  /**
   * Called when a text layer is rendered successfully
   */
  var onRenderSuccess = useCallback(function () {
    if (onRenderTextLayerSuccess) {
      onRenderTextLayerSuccess();
    }
  }, [onRenderTextLayerSuccess]);
  /**
   * Called when a text layer failed to render successfully
   */
  var onRenderError = useCallback(function (error) {
    warning(false, error.toString());
    if (onRenderTextLayerError) {
      onRenderTextLayerError(error);
    }
  }, [onRenderTextLayerError]);
  function onMouseDown() {
    var end = endElement.current;
    if (!end) {
      return;
    }
    end.classList.add('active');
  }
  function onMouseUp() {
    var end = endElement.current;
    if (!end) {
      return;
    }
    end.classList.remove('active');
  }
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  function renderTextLayer() {
    if (!page || !textContent) {
      return;
    }
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    layer.innerHTML = '';
    var textContentSource = page.streamTextContent({
      includeMarkedContent: true
    });
    var parameters = {
      container: layer,
      textContentSource: textContentSource,
      viewport: viewport
    };
    var cancellable = pdfjs.renderTextLayer(parameters);
    var runningTask = cancellable;
    cancellable.promise.then(function () {
      var end = document.createElement('div');
      end.className = 'endOfContent';
      layer.append(end);
      endElement.current = end;
      var layerChildren = layer.querySelectorAll('[role="presentation"]');
      if (customTextRenderer) {
        var index = 0;
        textContent.items.forEach(function (item, itemIndex) {
          if (!isTextItem(item)) {
            return;
          }
          var child = layerChildren[index];
          if (!child) {
            return;
          }
          var content = customTextRenderer(Object.assign({
            pageIndex: pageIndex,
            pageNumber: pageNumber,
            itemIndex: itemIndex
          }, item));
          child.innerHTML = content;
          index += item.str && item.hasEOL ? 2 : 1;
        });
      }
      // Intentional immediate callback
      onRenderSuccess();
    }).catch(onRenderError);
    return function () {
      return cancelRunningTask(runningTask);
    };
  }
  useLayoutEffect(renderTextLayer, [customTextRenderer, onRenderError, onRenderSuccess, page, pageIndex, pageNumber, textContent, viewport]);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      className: clsx('react-pdf__Page__textContent', 'textLayer'),
      onMouseUp: onMouseUp,
      onMouseDown: onMouseDown,
      ref: layerElement
    })
  );
}